import { getApolloAbtestResult, getDailyStickyBucketIndex } from '@/libs/utils/abtest'

/**
 * 将 AB 接口下发的 config 合并进 RaiseNewPayment 入参（浅合并 + 二层对象合并）
 * @param {Record<string, any>} base
 * @param {Record<string, any>|null|undefined} abConfig
 */
export function mergeRaisePaymentAbConfig(base, abConfig) {
  if (!abConfig || typeof abConfig !== 'object') return { ...base }
  return {
    ...base,
    ...abConfig,
    propStyleVar: {
      ...(base.propStyleVar || {}),
      ...(abConfig.propStyleVar || {}),
    },
    packagesContent: {
      ...(base.packagesContent || {}),
      ...(abConfig.packagesContent || {}),
    },
  }
}

/**
 * 带 Apollo AB 的拉起支付弹窗：请求实验 → 合并配置 → page_tp 前加分组标识（如 A/B/C）→ 调用 raiseFn
 *
 * 典型用法（运营位只需传 experimentId + pageTp + uid + 其余弹窗字段）：
 *
 *   await raisePaymentWithAb(this.$RaiseNewPayment, {
 *     experimentId: `${page_tp}_${this.role}`,
 *     uid: this.unionid || this.openid,
 *     pageTp: 'cnxh_vipsong_yzs',
 *     ...toastPayload,
 *   })
 *
 * @param {Function} raiseFn 一般为 this.$RaiseNewPayment
 * @param {Object} options
 * @param {string} options.experimentId Apollo experiment_id（与后台实验一致）
 * @param {string} [options.uid] unionid 或 openid；无 uid 时本地自然日分桶仅用于 A/B 前缀
 * @param {string} [options.pageTp] 未加 A/B 前的 page_tp；不传则用 options.page_tp 或 payload 里已有 page_tp
 * @param {boolean} [options.enableAb=true] 为 false 时等同直接 raiseFn，不加 AB
 * @param {Record<string, any>} options 其余字段原样传入 raiseFn（会与 ab config 合并）
 */
export async function raisePaymentWithAb(raiseFn, options) {
  if (typeof raiseFn !== 'function') return undefined

  const {
    experimentId,
    uid,
    pageTp,
    enableAb = true,
    ...payload
  } = options || {}

  const basePageTp = pageTp ?? payload.page_tp
  if (!enableAb || !experimentId) {
    return raiseFn({ ...payload, page_tp: basePageTp })
  }

  let group = 'A'
  let abConfig = null
  if (uid) {
    const res = await getApolloAbtestResult(experimentId, uid)
    // Apollo 可能返回 A/B/C...：page_tp 前缀应保持与服务端分组一致
    group = typeof res?.group === 'string' && res.group ? res.group : 'A'
    abConfig = res?.config
  } else {
    const bucket = getDailyStickyBucketIndex(`raise_ab_${experimentId}`, 'na', 2)
    group = bucket === 1 ? 'B' : 'A'
  }

  const merged = mergeRaisePaymentAbConfig(payload, abConfig)
  return raiseFn({
    ...merged,
    page_tp: `${group}${basePageTp}`,
  })
}
